import { useMotionPreference } from "@/hooks/useMotionPreference";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export default function FlowDiagram({
  steps,
  dark = false,
  label = "System / Cernio flow",
  testid = "flow-diagram",
}) {
  const ref = useRef(null);
  const reduced = useMotionPreference();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 85%", "end 55%"],
  });
  const fill = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const parallax = useTransform(scrollYProgress, [0, 1], [14, -14]);

  const shell = dark
    ? "border-white/10 bg-ink-soft"
    : "border-slate-200 bg-white";
  const node = dark
    ? "border-white/10 bg-white/[0.03] hover:border-white/25"
    : "border-slate-200 bg-paper hover:border-navy/40";
  const marker = dark
    ? "border-white/20 bg-ink-soft"
    : "border-slate-300 bg-white";
  const rail = dark ? "bg-white/10" : "bg-slate-200";
  const title = dark ? "text-slate-100" : "text-ink";
  const sub = dark ? "text-slate-500" : "text-slate-500";
  const chip = dark
    ? "border-white/10 text-slate-400"
    : "border-slate-200 text-slate-500";

  return (
    <div
      ref={ref}
      data-testid={testid}
      className={`relative border p-6 shadow-[0_24px_60px_-32px_rgba(11,15,23,0.25)] sm:p-8 ${shell}`}
    >
      <div className="flex items-center justify-between gap-4">
        <span
          className={`font-mono text-[11px] uppercase tracking-[0.2em] ${sub}`}
        >
          {label}
        </span>
        <span
          className={`flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] ${sub}`}
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping bg-gold opacity-60" />
            <span className="relative inline-flex h-1.5 w-1.5 bg-gold" />
          </span>
          Model
        </span>
      </div>

      <motion.div style={{ y: reduced ? 0 : parallax }} className="relative mt-8">
        <div
          className={`absolute bottom-3 left-[7px] top-3 w-px ${rail}`}
          aria-hidden="true"
        />
        <motion.div
          style={{ scaleY: reduced ? 1 : fill }}
          className={`absolute bottom-3 left-[7px] top-3 w-px origin-top ${
            dark ? "bg-cobalt" : "bg-navy"
          }`}
          aria-hidden="true"
        />
        <div className="space-y-3.5">
          {steps.map((s, i) => (
            <div key={s.title} className="relative flex items-stretch gap-5">
              <span
                className={`relative z-10 mt-4 grid h-[15px] w-[15px] shrink-0 place-items-center border ${marker}`}
                aria-hidden="true"
              >
                <span
                  className={`h-[5px] w-[5px] ${dark ? "bg-cobalt" : "bg-navy"}`}
                />
              </span>
              <div
                data-testid={`${testid}-node-${i + 1}`}
                className={`flex-1 border p-4 transition-colors duration-300 ${node}`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <div className="flex items-baseline gap-3">
                    <span
                      className={`font-mono text-[11px] tracking-[0.15em] ${sub}`}
                    >
                      {s.index}
                    </span>
                    <span
                      className={`font-display text-base font-bold tracking-tight ${title}`}
                    >
                      {s.title}
                    </span>
                  </div>
                  <span className={`font-mono text-[11px] ${sub}`}>
                    {s.desc}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {s.chips.map((c) => (
                    <span
                      key={c}
                      className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] ${chip}`}
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
